import { useState, useEffect } from "react";
import { Ruler, Plus, Trash2, Save, StickyNote, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { fmt } from "@/lib/format";

const uid = () => Math.random().toString(36).slice(2, 10);
const todayISO = () => new Date().toISOString().slice(0, 10);

function NoteButton({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const has = !!(value && value.trim());
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setDraft(value || ""); }}>
      <PopoverTrigger asChild>
        <button type="button" title={has ? "Modifica nota" : "Aggiungi nota"} className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors ${has ? "border-amber-400/50 bg-amber-400/15 text-amber-400" : "border-white/10 text-muted-foreground hover:text-primary"}`} data-testid={testid}><StickyNote className="h-4 w-4" /></button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-72 border-white/10 bg-[hsl(var(--popover))] text-foreground" data-testid={`${testid}-popover`}>
        <div className="space-y-2">
          <div className="text-xs font-semibold">Nota misurazione</div>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} placeholder="Scrivi una nota per questa misurazione…" className="w-full resize-none rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-primary/60" data-testid={`${testid}-input`} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setDraft(value || ""); setOpen(false); }} className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid={`${testid}-cancel`}>Annulla</button>
            <button type="button" onClick={() => { onChange(draft); setOpen(false); toast.success("Nota salvata"); }} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground" data-testid={`${testid}-save`}>Salva</button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const ANTROPO_FIELDS = ["PETTO", "VITA", "FIANCHI", "GAMBE", "BRACCIA"];
export const PLICO_FIELDS = ["TRICIPITALE", "SOTTOSCAPOLARE", "SOPRAILIACA", "ADDOMINALE", "PETTORALE", "COSCIA"];

export const emptyMeasurements = () => ({ type: "antropo", antropo: [], plico: [] });

const makeWeek = () => ({ id: uid(), date: todayISO(), values: {} });

export default function MisurazioniTab({ measurements, setMeasurements, onSave, onConfirmIncoming }) {
  const M = measurements && measurements.type ? measurements : emptyMeasurements();
  const type = M.type;
  const fields = type === "antropo" ? ANTROPO_FIELDS : PLICO_FIELDS;
  const unit = type === "antropo" ? "cm" : "mm";
  const weeks = M[type] || [];

  const setType = (t) => setMeasurements({ ...M, type: t });

  const addWeek = () =>
    setMeasurements({ ...M, [type]: [...weeks, makeWeek()] });

  const removeWeek = (id) =>
    setMeasurements({ ...M, [type]: weeks.filter((w) => w.id !== id) });

  const setValue = (weekId, field, val) =>
    setMeasurements({
      ...M,
      [type]: weeks.map((w) => (w.id === weekId ? { ...w, values: { ...w.values, [field]: val } } : w)),
    });

  const delta = (wi, field) => {
    if (wi === 0) return null;
    const cur = parseFloat(weeks[wi].values[field]);
    const prev = parseFloat(weeks[wi - 1].values[field]);
    if (!isFinite(cur) || !isFinite(prev)) return null;
    return cur - prev;
  };

  return (
    <div className="space-y-5" data-testid="tab-misurazioni">
      <section className="panel p-5 sm:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Ruler className="h-5 w-5 text-primary" /> Misurazioni
          </h2>
          <div className="flex gap-1 rounded-lg border border-white/10 bg-black/25 p-1">
            {[["antropo", "Antropometriche"], ["plico", "Plicometro"]].map(([v, l]) => (
              <button key={v} onClick={() => setType(v)} data-testid={`measure-type-${v}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${type === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>

        <p className="mb-4 text-xs text-muted-foreground">
          {type === "antropo"
            ? "Circonferenze corporee in centimetri, per data di misurazione."
            : "Pliche cutanee in millimetri (plicometro), per data di misurazione."}
        </p>

        {weeks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 py-10 text-center text-sm text-muted-foreground">
            Nessuna misurazione. Aggiungi la prima.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm" data-testid="measure-table">
              <thead>
                <tr className="border-b border-white/10 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-3 text-left font-semibold">Data misurazione</th>
                  <th className="px-2 py-2 text-center font-semibold">NOTE</th>
                  <th className="px-2 py-2 text-center font-semibold">PESO <span className="text-[9px] normal-case">(kg)</span></th>
                  {fields.map((f) => <th key={f} className="px-2 py-2 text-center font-semibold">{f} <span className="text-[9px] normal-case">({unit})</span></th>)}
                  <th className="py-2 pl-2"></th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((w, wi) => (
                  <tr key={w.id} className={`border-b border-white/5 ${w.incoming ? "bg-[hsl(43_74%_58%/0.12)] outline outline-1 outline-[hsl(43_74%_58%/0.6)]" : ""}`} data-testid={`measure-week-${wi}`} data-incoming={w.incoming ? "true" : "false"}>
                    <td className="py-2 pr-3">
                      <input type="date" value={w.date || ""} onChange={(e) => setMeasurements({ ...M, [type]: weeks.map((x) => x.id === w.id ? { ...x, date: e.target.value } : x) })} className="w-40 rounded bg-black/30 px-2 py-1.5 text-sm font-semibold outline-none focus:ring-1 focus:ring-primary/40" data-testid={`week-date-${wi}`} />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <NoteButton value={w.note} onChange={(v) => setMeasurements({ ...M, [type]: weeks.map((x) => x.id === w.id ? { ...x, note: v } : x) })} testid={`measure-${wi}-NOTE`} />
                    </td>
                    {(() => {
                      const d = delta(wi, "PESO");
                      return (
                        <td className="px-2 py-2 text-center">
                          <input type="number" value={w.values.PESO ?? ""} onChange={(e) => setValue(w.id, "PESO", e.target.value)} className="w-16 rounded bg-black/30 px-2 py-1.5 text-center font-mono-num text-sm outline-none focus:ring-1 focus:ring-primary/40" data-testid={`measure-${wi}-PESO`} />
                          {d !== null && (
                            <div className={`mt-0.5 font-mono-num text-[10px] ${d < 0 ? "text-[hsl(var(--c-prot))]" : d > 0 ? "text-[hsl(var(--c-fat))]" : "text-muted-foreground"}`}>
                              {d > 0 ? "+" : ""}{fmt(d, 1)}
                            </div>
                          )}
                        </td>
                      );
                    })()}
                    {fields.map((f) => {
                      const d = delta(wi, f);
                      return (
                        <td key={f} className="px-2 py-2 text-center">
                          <input type="number" value={w.values[f] ?? ""} onChange={(e) => setValue(w.id, f, e.target.value)} className="w-16 rounded bg-black/30 px-2 py-1.5 text-center font-mono-num text-sm outline-none focus:ring-1 focus:ring-primary/40" data-testid={`measure-${wi}-${f}`} />
                          {d !== null && (
                            <div className={`mt-0.5 font-mono-num text-[10px] ${d < 0 ? "text-[hsl(var(--c-prot))]" : d > 0 ? "text-[hsl(var(--c-fat))]" : "text-muted-foreground"}`}>
                              {d > 0 ? "+" : ""}{fmt(d, 1)}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-2 pl-2 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {w.incoming ? (
                          <button onClick={() => onConfirmIncoming(w.id)} title="Misure inviate dal cliente (media dei 3 valori)" className="inline-flex items-center gap-1 rounded-md bg-[hsl(43_74%_58%)] px-2 py-1.5 text-xs font-bold text-black" data-testid={`confirm-incoming-${wi}`}><Check className="h-3.5 w-3.5" /> Conferma</button>
                        ) : <button onClick={() => onSave && onSave()} className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20" data-testid={`save-week-${wi}`}><Save className="h-3.5 w-3.5" /> Salva</button>}
                        <button onClick={() => removeWeek(w.id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-week-${wi}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button onClick={addWeek} className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/20" data-testid="add-week-btn">
          <Plus className="h-4 w-4" /> Aggiungi Misurazione
        </button>
      </section>
    </div>
  );
}
